import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, LifeBuoy, Send, Video, VideoOff, Plus, CheckCircle2, ScreenShare } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS = {
  open: ["Waiting for the team", "bg-[#c9a961]/20 text-[#8a6d2f]"],
  active: ["Team is helping", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
  resolved: ["Resolved", "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"],
};

export default function PortalSupport() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ subject: "", detail: "" });
  const [text, setText] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [creating, setCreating] = useState(false);
  const endRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/support");
      setSessions(data.sessions || []);
      const open = (data.sessions || []).find((s) => s.status !== "resolved");
      if (open && !active) setActive(open);
    } catch { /* ignore */ }
  }, [active]);
  useEffect(() => { loadList(); }, [loadList]);

  const loadSession = useCallback(async (sid) => {
    try { const { data } = await api.get(`/portal/support/${sid}`); setActive(data); }
    catch { /* ignore */ }
  }, []);

  // Poll the active session for new messages.
  useEffect(() => {
    if (!active?.id) return;
    const iv = setInterval(() => loadSession(active.id), 3000);
    return () => clearInterval(iv);
  }, [active?.id, loadSession]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages?.length]);

  const create = async () => {
    if (!form.subject.trim()) { toast.error("Add a short subject."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/support", form);
      setForm({ subject: "", detail: "" }); setCreating(false); setActive(data);
      await loadList();
      toast.success("Help request sent — the team has been notified.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the request."); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    const t = text; setText("");
    try { await api.post(`/portal/support/${active.id}/messages`, { text: t }); await loadSession(active.id); }
    catch { toast.error("Could not send message."); setText(t); }
  };

  const inp = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";
  const showForm = creating || (!active && sessions.length === 0);

  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="portal-support">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-[#1e3d3a]"><LifeBuoy className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Get help</h3></div>
        {active && !showForm && (
          <button data-testid="support-new" onClick={() => { setCreating(true); setActive(null); }} className="inline-flex items-center gap-1 text-xs text-[#a3823f] hover:underline"><Plus className="h-3.5 w-3.5" /> New request</button>
        )}
      </div>
      <p className="text-xs text-[#1e3d3a]/55 mb-4">Stuck on something? Send us a request and chat live with the Elevare team — we can hop on a video call and fix it with you.</p>

      {showForm ? (
        <div className="space-y-2">
          <input data-testid="support-subject" className={inp} placeholder="What do you need help with?" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea data-testid="support-detail" className={`${inp} resize-none`} rows={3} placeholder="Tell us what's happening…" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} />
          <div className="flex gap-2">
            <button data-testid="support-submit" onClick={create} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Request help</button>
            {sessions.length > 0 && <button onClick={() => { setCreating(false); loadList(); }} className="rounded-full border border-[#1e3d3a]/15 px-4 py-2 text-sm text-[#1e3d3a]/70">Cancel</button>}
          </div>
        </div>
      ) : active ? (
        <div data-testid="support-session">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-[#1e3d3a] truncate">{active.subject}</div>
            <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${(STATUS[active.status] || STATUS.open)[1]}`}>{(STATUS[active.status] || STATUS.open)[0]}</span>
          </div>

          <button data-testid="support-video-toggle" onClick={() => { const next = !showVideo; setShowVideo(next); if (next) toast.info("Please click \"Share screen\" in the call so the Elevare team can see your account.", { duration: 7000 }); }} className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972]">
            {showVideo ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />} {showVideo ? "Leave video" : "Start video call"}
          </button>
          {showVideo && (
            <div className="mb-3 overflow-hidden rounded-xl border border-[#1e3d3a]/15" data-testid="support-video-frame">
              <div className="flex items-center gap-2 bg-[#c9a961]/20 px-3 py-2 text-[12px] text-[#8a6d2f]" data-testid="support-screenshare-nudge">
                <ScreenShare className="h-4 w-4" /> Tap <b>"Share screen"</b> in the call so we can see exactly what's happening in your account.
              </div>
              <iframe title="Elevare support video" src={`https://meet.jit.si/${active.video_room}#config.prejoinPageEnabled=false`}
                allow="camera; microphone; fullscreen; display-capture; autoplay" className="w-full" style={{ height: 380, border: 0 }} />
            </div>
          )}

          <div className="max-h-72 overflow-y-auto rounded-xl border border-[#1e3d3a]/10 bg-[#f7f5ef] p-3 space-y-2" data-testid="support-messages">
            {(active.messages || []).map((m) => (
              <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "client" ? "bg-[#1e3d3a] text-white" : "bg-white border border-[#1e3d3a]/10 text-[#1e3d3a]"}`}>
                  {m.sender !== "client" && <div className="text-[10px] uppercase tracking-wide text-[#a3823f] mb-0.5">{m.sender_name}</div>}
                  {m.text}
                </div>
              </div>
            ))}
            {active.status === "resolved" && <div className="flex items-center justify-center gap-1.5 text-xs text-[#2f7d5b] pt-1"><CheckCircle2 className="h-4 w-4" /> This request was marked resolved.</div>}
            <div ref={endRef} />
          </div>

          <div className="mt-2 flex gap-2">
            <input data-testid="support-message-input" className={inp} placeholder="Type a message…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} />
            <button data-testid="support-message-send" onClick={send} className="rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d]"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="support-history">
          {sessions.map((s) => (
            <button key={s.id} data-testid={`support-session-${s.id}`} onClick={() => loadSession(s.id)} className="w-full flex items-center justify-between rounded-lg border border-[#1e3d3a]/10 px-3 py-2 text-left hover:border-[#c9a961]">
              <span className="text-sm text-[#1e3d3a] truncate">{s.subject}</span>
              <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${(STATUS[s.status] || STATUS.open)[1]}`}>{(STATUS[s.status] || STATUS.open)[0]}</span>
            </button>
          ))}
          <button data-testid="support-new-2" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972]"><Plus className="h-4 w-4" /> New help request</button>
        </div>
      )}
    </div>
  );
}
